import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Container, HomeLink, ProgressBar, Text, Title} from '../components/@FreeModel';
import {
  Application,
  gql,
  queryApplication,
} from "../db/model";
import {useDispatch} from "react-redux";
import Footer from "../components/general/Footer";
import {isCorrectEmail} from "./validate";
import {pathIncorrectEmail} from "../routes";
import {Mixpanel} from "../observability/Mixpanel";

const deriveProgress = (application: Application): number => {
  if (application.hasVerifiedPhoneNumber !== true) {
    return 0
  } else if ((application.trades?.length ?? 0) === 0) {
    return 1
  } else if (!(application.hasAcceptedContract ?? false)) {
    return 2
  } else if (!(application.hasBankInfo ?? false)) {
    return 3
  } else {
    return 4
  }
}

const Home = (): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {applicationID} = useParams();
  const [loading, setLoading] = useState(true)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (applicationID && loading) {
      // @ts-ignore
      dispatch(gql({
        query: queryApplication,
        variables: {
          id: applicationID,
        },
        useSignupURL: true,
        onSuccess: (newState) => {
          if (!isCorrectEmail(newState)) {
            navigate(pathIncorrectEmail(applicationID))
            return
          }

          setProgress(deriveProgress(newState.application ?? {}))
          setLoading(false)
          Mixpanel.identify(applicationID)
        }
      }))
    }

  }, [dispatch, applicationID, loading])

  if (loading) {
    return <div></div>
  }

  return (
    <Container className="home">
      <ProgressBar progress={progress}/>
      <br/>
      {progress < 4 ? (
        <>
          <br/>
          <Title size="xl" weight="bold" lineHeight="lg">
            Getting Started
          </Title>
          <Text color="gray">In order to work with Freemodel, we need some additional information.</Text>
          <br/>
          <HomeLink
            to={`/contractors/${applicationID}/contact-info`}
            description="Tell us the best way to contact you about projects"
            index={1}
            active={progress === 0}
            completed={progress > 0}
          >
            Add Contact Info
          </HomeLink>
          <HomeLink
            to={`/contractors/${applicationID}/business-info/1`}
            description="Tell us about the details of your business"
            index={2}
            active={progress === 1}
            completed={progress > 1}
          >
            Add Business Info
          </HomeLink>
          <HomeLink
            to={`/contractors/${applicationID}/tax-info`}
            description="Tell us about the tax details of your business"
            index={3}
            active={progress === 2}
            completed={progress > 2}
          >
            Add Tax Info
          </HomeLink>
          <HomeLink
            to={`/contractors/${applicationID}/banking-info`}
            description="Connect your bank account via Plaid to receive payments"
            index={4}
            active={progress === 3}
            completed={progress > 3}
          >
            Add Banking Info
          </HomeLink>

          <br/>

          <Footer/>
        </>
      ) : (
        <>
          <Title weight="bold" lineHeight="lg" color="green" size="xl">
            You’re all set!
          </Title>
          <br/>
          <Text lineHeight="md" color="gray">
            You’re ready to work on Freemodel projects. If you have any questions or need any help, contact us at
            <Text
              size="sm"
              color="blue"
              lineHeight="sm"
              style={{cursor: 'pointer'}}
              inline
              onClick={() => {
                window.location.href = "mailto:contractors@freemodel.com"
              }}
            >
              contractors@freemodel.com
            </Text>
            .
          </Text>
        </>
      )}
    </Container>
  );
};

export default Home;
